import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";

import { toast } from "../../services/toast";
import { useParams, useNavigate } from "react-router-dom";

import { makeStyles } from 'tss-react/mui';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";

import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from '@mui/icons-material/Clear';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Tooltip from '@mui/material/Tooltip';
import ImportFromTicketsDialog from "../../components/ImportFromTicketsDialog";
import ForumIcon from '@mui/icons-material/Forum';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListItemModal from "../../components/ContactListItemModal";
import ConfirmationModal from "../../components/ConfirmationModal/";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import useContactLists from "../../hooks/useContactLists";

import chat_plantilla from "../../assets/chat_plantilla.xlsx";
import { socketConnection } from "../../services/socket";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;

    if (!Array.isArray(contacts)) return state;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    width: "100%",
    flexWrap: "wrap",
  },
  titulo: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(1),
  },
  total: {
    fontSize: 18,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  totalTexto: {
    fontSize: 12.5,
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  acciones: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  buscador: {
    minWidth: 260,
    "& .MuiOutlinedInput-root": {
      backgroundColor:
        theme.palette.mode === "light"
          ? theme.palette.background.paper
          : "rgba(255,255,255,0.04)",
    },
  },
  btn: {
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  tabla: {
    "& th": {
      fontWeight: 700,
      fontSize: 12,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
    },
  },
  tablaCabecera: {
    backgroundColor:
      theme.palette.mode === "light"
        ? theme.palette.grey[100]
        : "rgba(255,255,255,0.05)",
  },
  celdaEstado: {
    width: 90,
  },
  celdaNombre: {
    fontWeight: 600,
    width: "35%",
  },
  celdaEmail: {
    color: theme.palette.text.secondary,
    maxWidth: 280,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  num: {
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  celdaAcciones: {
    whiteSpace: "nowrap",
  },
  iconoValido: {
    color: theme.palette.success.main,
    verticalAlign: "middle",
  },
  iconoInvalido: {
    color: theme.palette.text.disabled,
    verticalAlign: "middle",
  },
  vacio: {
    color: theme.palette.text.disabled,
    fontStyle: "italic",
  },
  tablaVacia: {
    padding: theme.spacing(4, 2),
    color: theme.palette.text.secondary,
    fontSize: 13.5,
  },
}));

const ContactListItems = () => {
  const { classes } = useStyles();

  const { user } = useContext(AuthContext);
  const { contactListId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactListItemModalOpen, setContactListItemModalOpen] =
    useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [importarOpen, setImportarOpen] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [contactList, setContactList] = useState({});
  const fileUploadRef = useRef(null);

  const { findById: findContactList } = useContactLists();

  useEffect(() => {
    findContactList(contactListId).then((data) => {
      setContactList(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get(`contact-list-items`, {
            params: { searchParam, pageNumber, contactListId },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, contactListId, recarga]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-ContactListItem`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.id });
      }

    });

    socket.on(
      `company-${companyId}-ContactListItem-${contactListId}`,
      (data) => {
        if (data.action === "reload") {
          if (Array.isArray(data.records)) {
            dispatch({ type: "LOAD_CONTACTS", payload: data.records });
          } else {
            dispatch({ type: "RESET" });
            setRecarga((n) => n + 1);
          }
        }
      }
    );

    return () => {
      socket.disconnect();
    };
  }, [contactListId]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(true);
  };

  const handleCloseContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactListItemModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contact-list-items/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleImportContacts = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);
      await api.request({
        url: `contact-lists/${contactListId}/upload`,
        method: "POST",
        data: formData,
      });
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContactLists = () => {
    navigate("/contact-lists");
  };

  return (
    <MainContainer className={classes.mainContainer} maxWidth={false}>
      <ImportFromTicketsDialog
        open={importarOpen}
        onClose={() => setImportarOpen(false)}
        contactListId={contactListId}
        onDone={() => {
          dispatch({ type: "RESET" });
          setPageNumber(1);
          setRecarga((n) => n + 1);
        }}
      />

      <ContactListItemModal
        open={contactListItemModalOpen}
        onClose={handleCloseContactListItemModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactListItemModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contactListItems.confirmationModal.deleteTitle")} ${
                deletingContact.name
              }?`
            : `${i18n.t("contactListItems.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleImportContacts()
        }
      >
        {deletingContact ? (
          `${i18n.t("contactListItems.confirmationModal.deleteMessage")}`
        ) : (
          <>
            {i18n.t("contactListItems.confirmationModal.importMessage")}
            <a href={chat_plantilla} download="chat_plantilla.xlsx">
              Click aqui para descargar plantilla.
            </a>
          </>
        )}
      </ConfirmationModal>
      <MainHeader>
        <div className={classes.header}>
          <div className={classes.titulo}>
            <Title>{contactList.name}</Title>
            <span className={classes.total}>
              {contacts.length}
              <span className={classes.totalTexto}>
                {contacts.length === 1 ? " contacto" : " contactos"}
              </span>
            </span>
          </div>

          <div className={classes.acciones}>
            <TextField
              className={classes.buscador}
              size="small"
              variant="outlined"
              placeholder={i18n.t("contactListItems.searchPlaceholder")}
              value={searchParam}
              onChange={handleSearch}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                  endAdornment: searchParam ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => setSearchParam("")}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }
              }}
            />

            <Tooltip title={i18n.t("contactListItems.buttons.lists")}>
              <IconButton onClick={goToContactLists}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>

            <Button
              variant="outlined"
              startIcon={<ForumIcon />}
              className={classes.btn}
              onClick={() => setImportarOpen(true)}
            >
              Desde tickets
            </Button>

            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              className={classes.btn}
              onClick={() => {
                fileUploadRef.current.value = null;
                fileUploadRef.current.click();
              }}
            >
              {i18n.t("contactListItems.buttons.import")}
            </Button>

            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.btn}
              onClick={handleOpenContactListItemModal}
            >
              {i18n.t("contactListItems.buttons.add")}
            </Button>
          </div>
        </div>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <>
          <input
            style={{ display: "none" }}
            id="upload"
            name="file"
            type="file"
            accept=".xls,.xlsx"
            onChange={() => {
              setConfirmOpen(true);
            }}
            ref={fileUploadRef}
          />
        </>
        <Table size="small" className={classes.tabla}>
          <TableHead className={classes.tablaCabecera}>
            <TableRow>
              <TableCell align="center" className={classes.celdaEstado}>
                WhatsApp
              </TableCell>
              <TableCell align="left">
                {i18n.t("contactListItems.table.name")}
              </TableCell>
              <TableCell align="right">
                {i18n.t("contactListItems.table.number")}
              </TableCell>
              <TableCell align="left">
                {i18n.t("contactListItems.table.email")}
              </TableCell>
              <TableCell align="right">
                {i18n.t("contactListItems.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contacts.map((contact) => (
                <TableRow key={contact.id} hover>
                  <TableCell align="center" className={classes.celdaEstado}>
                    <Tooltip
                      title={
                        contact.isWhatsappValid
                          ? "Tiene WhatsApp"
                          : "Sin WhatsApp: no recibirá la campaña"
                      }
                    >
                      {contact.isWhatsappValid ? (
                        <CheckCircleIcon
                          fontSize="small"
                          className={classes.iconoValido}
                        />
                      ) : (
                        <BlockIcon
                          fontSize="small"
                          className={classes.iconoInvalido}
                        />
                      )}
                    </Tooltip>
                  </TableCell>

                  <TableCell align="left" className={classes.celdaNombre}>
                    {contact.name || (
                      <span className={classes.vacio}>Sin nombre</span>
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.num}>
                    {contact.number}
                  </TableCell>
                  <TableCell align="left" className={classes.celdaEmail}>
                    {contact.email || (
                      <span className={classes.vacio}>—</span>
                    )}
                  </TableCell>

                  <TableCell align="right" className={classes.celdaAcciones}>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => hadleEditContact(contact.id)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Can
                      role={user.profile}
                      perform="contacts-page:deleteContact"
                      yes={() => (
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setConfirmOpen(true);
                              setDeletingContact(contact);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}

              {!loading && contacts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <div className={classes.tablaVacia}>
                      {searchParam
                        ? `Ningún contacto coincide con "${searchParam}".`
                        : "Esta lista todavía no tiene contactos. Impórtalos desde un Excel o agrégalos uno a uno."}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {loading && <TableRowSkeleton columns={5} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ContactListItems;
